import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Tag, ExternalLink, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog/article/${slug}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Error fetching article:', error);
      setError('Articolo non trovato');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-6 py-16 max-w-4xl">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="h-64 bg-gray-200 rounded-2xl mb-8"></div>
            <div className="h-12 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="space-y-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="h-8 w-8 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Articolo non trovato</h1>
          <p className="text-gray-600 mb-6">L'articolo che stai cercando non esiste o è stato rimosso.</p>
          <Link to="/blog">
            <Button>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Torna al Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/logo-originale.png" 
                alt="InLeads Logo" 
                className="w-8 h-8 object-contain"
              />
              <span className="text-2xl font-bold text-gray-900">InLeads</span>
            </Link>
            
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Home
              </Link>
              <Link to="/blog" className="text-[#0047FF] font-semibold">
                Blog
              </Link>
              <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Accedi all'App
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="container mx-auto px-6 py-4 max-w-4xl">
        <nav className="flex items-center space-x-2 text-sm">
          <Link to="/blog" className="text-[#0047FF] hover:underline flex items-center">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Blog
          </Link>
          <span className="text-gray-500">/</span>
          <span className="text-gray-700">{article.title}</span>
        </nav>
      </div>

      {/* Article */}
      <article className="container mx-auto px-6 pb-16 max-w-4xl">
        {/* Cover Image */}
        <div className="relative mb-8">
          <img
            src={article.cover_image}
            alt={article.title}
            className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-lg"
          />
          <div className="absolute top-6 left-6">
            <Badge className="bg-[#0047FF] text-white text-sm px-3 py-1">
              {article.category}
            </Badge>
          </div>
        </div>

        {/* Article Header */}
        <header className="mb-8">
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {article.title}
          </h1>
          
          <div className="flex flex-wrap items-center text-gray-600 space-x-6 mb-6">
            <div className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <span className="font-medium">{article.author}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5" />
              <span>{formatDate(article.published_date)}</span>
            </div>
          </div>

          {article.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-sm">
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </header>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          <div 
            className="text-gray-700 leading-relaxed mb-8"
            dangerouslySetInnerHTML={{ __html: article.preview_content }}
          />
        </div>

        {/* Paywall CTA */}
        <Card className="border-2 border-[#0047FF] bg-gradient-to-br from-blue-50 to-indigo-50 my-12">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-[#0047FF] rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="h-8 w-8 text-white" />
            </div>
            
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              🔒 Vuoi leggere la guida completa e accedere ai contenuti video?
            </h3>
            
            <p className="text-lg text-gray-700 mb-6">
              👉 Registrati gratis su InLeads e continua la lettura nell'app.
            </p>
            
            <div className="space-y-4">
              <Button 
                size="lg" 
                className="bg-[#0047FF] hover:bg-[#07396B] text-white px-8 py-4 text-lg rounded-xl font-bold w-full md:w-auto"
                asChild
              >
                <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center">
                  <ExternalLink className="mr-2 h-5 w-5" />
                  Accedi all'app InLeads
                </a>
              </Button>
              
              <p className="text-sm text-gray-600">
                Accesso gratuito • Contenuti esclusivi • Opportunità di lavoro
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-r from-[#0047FF] to-[#07396B] rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">
            Continua la lettura sull'app InLeads
          </h3>
          <p className="text-lg mb-6 opacity-90">
            Accedi a guide complete, video formativi e opportunità di lavoro esclusive
          </p>
          <Button 
            size="lg" 
            className="bg-white text-[#0047FF] px-8 py-4 text-lg rounded-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 font-bold"
            asChild
          >
            <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer">
              Registrati Gratis
            </a>
          </Button>
        </div>
      </article>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-50 border-t mt-16">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img 
                src="/logo-originale.png" 
                alt="InLeads Logo" 
                className="w-8 h-8 object-contain"
              />
              <span className="text-2xl font-bold text-gray-900">InLeads</span>
            </div>
            <p className="text-gray-600 mb-6">
              La piattaforma che connette agenzie e talenti nel settore immobiliare
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8">
            <Link to="/" className="text-gray-600 hover:text-[#0047FF] transition-colors">
              Home
            </Link>
            <Link to="/blog" className="text-gray-600 hover:text-[#0047FF] transition-colors">
              Blog
            </Link>
            <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#0047FF] transition-colors">
              App InLeads
            </a>
            <Link to="https://app.inleads.it/privacy" className="text-gray-600 hover:text-[#0047FF] transition-colors">
              Privacy Policy
            </Link>
          </div>

          <div className="text-center mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              © 2025 InLeads. Tutti i diritti riservati.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogArticle;